module Meevo.Components.Controls {

    import ComparedToEndChoicesEnum = Meevo.Components.Common.ComparedToEndChoicesEnum;
    import IDateRangeObject = Meevo.Components.Common.IDateRangeObject;
    import DateRangeService = Meevo.Components.Common.DateRangeService;

    interface ICallbackFnArgs {
        fromDate?: Date;
        toDate?: Date;
        selectedStartDate?: Date;
        selectedEndDate?: Date;
    }

    export interface IComparedToDateChoice {
        name: string;
        id: ComparedToEndChoicesEnum;
        callbackFn(args: ICallbackFnArgs): IDateRangeObject;
    }

    export interface IChoiceWithAdditionalControl extends IComparedToDateChoice {
        additionalControl: string;
        controlMapFn(v: any): IDateRangeObject;
    }

    abstract class ComparedToChoicesBase implements IComparedToDateChoice {
        abstract name: string;
        abstract id: ComparedToEndChoicesEnum;

        protected dateRangeService: DateRangeService;

        constructor(dateRangeService: DateRangeService) {
            this.dateRangeService = dateRangeService;
        }

        abstract callbackFn(args: ICallbackFnArgs): IDateRangeObject;
    }

    abstract class ChoiceWithAdditionalControl extends ComparedToChoicesBase implements IChoiceWithAdditionalControl {
        abstract additionalControl: string;

        abstract controlMapFn(v: any): IDateRangeObject;
    }

    export class PreviousDayChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Day';
        id = ComparedToEndChoicesEnum.PreviousDay;

        callbackFn({ fromDate }): IDateRangeObject {
            return this.dateRangeService.previousDay(fromDate);
        }
    }

    export class SameDayPreviousMonthChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Day, Previous Month';
        id = ComparedToEndChoicesEnum.SameDayPreviousMonth;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousMonth(fromDate, toDate);
        }
    }

    export class SameDayPreviousWeekChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Day, Previous Week';
        id = ComparedToEndChoicesEnum.SameDayPreviousWeek;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousWeek(fromDate, toDate);
        }
    }

    export class SameDayPreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Day, Previous Year';
        id = ComparedToEndChoicesEnum.SameDayPreviousYear;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousYear(fromDate, toDate);
        }
    }

    export class SpecificDateChoice extends ChoiceWithAdditionalControl implements IChoiceWithAdditionalControl {
        name = 'Specific Date';
        id = ComparedToEndChoicesEnum.SpecificDate;
        additionalControl = 'singleDateSelector';

        controlMapFn(date: Date): IDateRangeObject {
            return {
                from: moment(date).startOf('day').toDate(),
                to: moment(date).endOf('day').toDate()
            }
        }

        callbackFn() {
            return this.dateRangeService.today();
        }
    }

    export class PreviousWeekChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Week';
        id = ComparedToEndChoicesEnum.PreviousWeek;

        callbackFn({ fromDate }) {
            return this.dateRangeService.previousWeek(
                moment(fromDate).endOf('day').toDate()
            );
        }
    }

    export class SameWeekPreviousMonthChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Week, Previous Month';
        id = ComparedToEndChoicesEnum.SameWeekPreviousMonth;

        callbackFn({ toDate, selectedEndDate }) {
            return !selectedEndDate
                ? this.dateRangeService.thisWeek(
                    moment(toDate).subtract(1, 'month').toDate()
                )
                : this.dateRangeService.thisWeek(
                    moment(selectedEndDate).subtract(2, 'month').toDate()
                )
        }
    }

    export class SameWeekPreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Week, Previous Year';
        id = ComparedToEndChoicesEnum.SameWeekPreviousYear;

        callbackFn({ toDate, selectedEndDate }) {
            return !selectedEndDate
                ? this.dateRangeService.thisWeek(
                    moment(toDate).subtract(1, 'year').toDate()
                )
                : this.dateRangeService.thisWeek(
                    moment(selectedEndDate).subtract(2, 'year').toDate()
                )
        }
    }

    export class PreviousMonthChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Month';
        id = ComparedToEndChoicesEnum.PreviousMonth;

        callbackFn({ fromDate }) {
            return this.dateRangeService.previousMonth(fromDate);
        }
    }

    export class SameMonthPreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Month, Previous Year';
        id = ComparedToEndChoicesEnum.SameMonthPreviousYear;

        callbackFn({ fromDate }) {
            const { from, to } = this.dateRangeService.monthToDate(
                fromDate,
                moment(fromDate).endOf('month').toDate()
            );

            const prevYear = this.dateRangeService.previousYear(from, to);

            return {
                from: prevYear.from,
                to: moment(prevYear.to).endOf('month').toDate()
            }
        }
    }

    export class SpecificMonthChoice extends ChoiceWithAdditionalControl implements IChoiceWithAdditionalControl {
        name = 'Specific Month';
        id = ComparedToEndChoicesEnum.SpecificMonth;
        additionalControl = 'singleMonthSelector';

        callbackFn({ fromDate }) {
            return this.dateRangeService.previousDay(fromDate);
        }

        controlMapFn(date: Date): IDateRangeObject {
            return this.dateRangeService.monthToDate(date, moment(date).endOf('month').toDate());
        }
    }

    export class PreviousQuarterChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Quarter';
        id = ComparedToEndChoicesEnum.PreviousQuarter;

        callbackFn({ fromDate }) {
            return this.dateRangeService.previousQuarter(fromDate);
        }
    }

    export class SameQuarterPreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Quarter, Previous Year';
        id = ComparedToEndChoicesEnum.SameQuarterPreviousYear;

        callbackFn({ fromDate }) {
            const { from, to } = this.dateRangeService.getQuarter(false, fromDate);
            return this.dateRangeService.previousYear(from, to);
        }
    }

    export class PreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Year';
        id = ComparedToEndChoicesEnum.PreviousYear;

        callbackFn({ fromDate }) {
            return this.dateRangeService.previousYear(
                fromDate,
                moment(fromDate).endOf('year').toDate()
            );
        }
    }

    export class SpecificYearChoice extends ChoiceWithAdditionalControl implements IChoiceWithAdditionalControl {
        name = 'Specific Year';
        id = ComparedToEndChoicesEnum.SpecificYear;
        additionalControl = 'singleYearSelector';

        callbackFn() {
            return this.dateRangeService.thisYear();
        }

        controlMapFn(year: number): IDateRangeObject {
            return {
                from: moment().year(year).startOf('year').toDate(),
                to: moment().year(year).endOf('year').toDate()
            };
        }
    }

    export class PreviousMonthToDateChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Month to Date';
        id = ComparedToEndChoicesEnum.PreviousMonthToDate;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousMonth(fromDate, toDate);
        }
    }

    export class PreviousYearToDateChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Year to Date';
        id = ComparedToEndChoicesEnum.PreviousYearToDate;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousYear(fromDate, toDate);
        }
    }

    export class SpecificYearToDateChoice extends ChoiceWithAdditionalControl implements IChoiceWithAdditionalControl {
        name = 'Specific Year to Date';
        id = ComparedToEndChoicesEnum.SpecificYearToDate;
        additionalControl = 'singleYearSelector';

        callbackFn() {
            return this.dateRangeService.thisYear();
        }

        controlMapFn(year: number): IDateRangeObject {
            return this.dateRangeService.specificYearToDate(
                moment().year(year).toDate(),
                moment().year(year).toDate()
            );
        }
    }

    export class SameMonthToDatePreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Month to Date, Previous Year';
        id = ComparedToEndChoicesEnum.SameMonthToDatePreviousYear;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousYear(fromDate, toDate);
        }
    }

    export class SpecificMonthToDateChoice extends ChoiceWithAdditionalControl implements IChoiceWithAdditionalControl {
        name = 'Specific Month to Date';
        id = ComparedToEndChoicesEnum.SpecificMonthToDate;
        additionalControl = 'singleMonthSelector';

        callbackFn({ fromDate }) {
            return this.dateRangeService.previousDay(fromDate);
        }

        controlMapFn(date: Date): IDateRangeObject {
            return this.dateRangeService.monthToDate(date);
        }
    }

    export class PreviousQuarterToDateChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Quarter to Date';
        id = ComparedToEndChoicesEnum.PreviousQuarterToDate;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousQuarterToDate(fromDate, toDate);
        }
    }

    export class SameQuarterToDatePreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Quarter to Date, Previous Year';
        id = ComparedToEndChoicesEnum.SameQuarterToDatePreviousYear;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousYear(fromDate, toDate);
        }
    }

    export class PreviousWeekToDateChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Previous Week to Date';
        id = ComparedToEndChoicesEnum.PreviousWeekToDate;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousWeekToDate(fromDate, toDate);
        }
    }

    export class SameWeekToDatePreviousMonthChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Week to Date, Previous Month';
        id = ComparedToEndChoicesEnum.SameWeekToDatePreviousMonth;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousMonth(fromDate, toDate);
        }
    }

    export class SameWeekToDatePreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Week to Date, Previous Year';
        id = ComparedToEndChoicesEnum.SameWeekToDatePreviousYear;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousYear(fromDate, toDate);
        }
    }
	
	export class SameDateRangePreviousYearChoice extends ComparedToChoicesBase implements IComparedToDateChoice {
        name = 'Same Date Range, Previous Year';
        id = ComparedToEndChoicesEnum.SameDateRangePreviousYear;

        callbackFn({ fromDate, toDate }) {
            return this.dateRangeService.previousYear(fromDate, toDate);
        }
    }
}